/**
 * A game, as a mark.
 *
 * Every place that shows a game - party cards, the profile, the create dialog,
 * the editor - draws it through here, so a game looks the same everywhere and
 * a new one only has to be added to the catalogue.
 */

import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Tip } from './HoverCard';
import { gameIconArt } from '@/lib/gameArt';

export function GameMark({ id, size = 28, className, plain, sub }: {
  id: string;
  size?: number;
  className?: string;
  /** Second line in the tooltip - a rank, or what the mark is standing for. */
  sub?: string;
  /** Just the glyph, no tinted plate - for use inside something already tinted. */
  plain?: boolean;
}) {
  const game = useSession((s) => s.catalog?.games.find((g) => g.id === id));
  const colour = game?.color ?? 'var(--accent)';
  const art = gameIconArt(id);
  const glyph = art
    ? <img src={art} alt="" loading="lazy" className="h-full w-full rounded-[inherit] object-cover" />
    : <Icon name={game?.icon ?? 'dice'} size={Math.round(size * 0.62)} />;

  if (plain) {
    /* Still a box of the size asked for: the glyph is often a picture, and a
       picture with nothing holding it draws itself at whatever size it was
       saved at. */
    return (
      <span
        className={cn('grid flex-none place-items-center overflow-hidden rounded-[28%]', className)}
        style={{ width: size, height: size, color: colour }}
      >
        {glyph}
      </span>
    );
  }

  return (
    <Tip text={game?.name ?? id} sub={sub}>
    <span
      /* Every game mark says which game it is; one delegated listener turns
         that into the parties for it - see lib/profileClicks. */
      data-game={id}
      className={cn('grid flex-none place-items-center overflow-hidden rounded-[28%] transition-transform duration-200 hover:-translate-y-0.5', className)}
      style={{
        width: size,
        height: size,
        color: colour,
        background: `color-mix(in srgb, ${colour} 16%, transparent)`,
        boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${colour} 26%, transparent)`
      }}
    >
      {glyph}
    </span>
    </Tip>
  );
}

/** The small pill used to list somebody's games, with their rank if they set one. */
export function GameChip({ id, rank, className }: { id: string; rank?: string; className?: string }) {
  const game = useSession((s) => s.catalog?.games.find((g) => g.id === id));
  const colour = game?.color ?? 'var(--accent)';

  return (
    <Tip text={game?.name ?? id} sub={rank || 'No rank set'}>
    <span
      data-game={id}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11.5px] font-semibold',
        className
      )}
      style={{
        color: colour,
        borderColor: `color-mix(in srgb, ${colour} 34%, transparent)`,
        background: `color-mix(in srgb, ${colour} 12%, transparent)`
      }}
    >
      <Icon name={game?.icon ?? 'dice'} size={12} stroke={2} />
      <span className="text-ink">{rank || game?.short || id}</span>
    </span>
    </Tip>
  );
}
